import React, { useState, useEffect } from 'react';
import { Form, Input, Button, InputNumber, Select, message, Modal, Row, Col, Card, Space, Tag, Empty, Avatar } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import SchoolNameInput from '../../components/SchoolNameInput/SchoolNameInput';
import EducationLevelInput from '../../components/EducationLevelInput/EducationLevelInput';

const { Option } = Select;

const EditLinkedStudentForm = ({ visible, onClose, mainUserId, onUpdated }) => {
  const [form] = Form.useForm();
  const [linkedStudents, setLinkedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch linked students for the main user
  const fetchLinkedStudents = async () => {
    if (!mainUserId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/linked-student/?main_user_id=${mainUserId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLinkedStudents(response.data);
    } catch (err) {
      console.error('Error fetching linked students:', err);
      message.error('Failed to fetch linked students');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && mainUserId) {
      fetchLinkedStudents();
    }
  }, [visible, mainUserId]);

  // Handle form setup when form modal opens
  useEffect(() => {
    if (formModalVisible && selectedStudent) {
      // Small delay to ensure form is fully rendered
      setTimeout(() => {
        form.setFieldsValue({
          firstname: selectedStudent.firstname || '',
          lastname: selectedStudent.lastname || '',
          nickname: selectedStudent.nickname || '',
          gender: selectedStudent.gender || undefined,
          age: selectedStudent.age || undefined,
          currenteducationlevel: selectedStudent.currenteducationlevel || '',
          schoolname: selectedStudent.schoolname || '',
        });
      }, 100);
    } else if (formModalVisible && !selectedStudent) {
      // Adding new student - reset form
      form.resetFields();
    }
  }, [selectedStudent, form, formModalVisible]);

  // Handle adding new student
  const handleAddStudent = () => {
    setSelectedStudent(null);
    setFormModalVisible(true);
  };

  // Handle editing existing student
  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setFormModalVisible(true);
  };

  // Handle deleting student
  const handleDeleteStudent = (student) => {
    Modal.confirm({
      title: 'Delete Linked Student',
      content: `Are you sure you want to delete "${student.firstname} ${student.lastname}"?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      async onOk() {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/linked-student/${student.id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          message.success('Linked student deleted successfully');
          fetchLinkedStudents(); // Refresh the list
          if (onUpdated) onUpdated(); // Notify parent component
        } catch (err) {
          console.error('Delete failed:', err);
          message.error('Failed to delete linked student');
        }
      },
    });
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      const token = localStorage.getItem('token');
      
      // Prepare the payload
      const payload = {
        firstname: values.firstname,
        lastname: values.lastname,
        nickname: values.nickname,
        gender: values.gender || null,
        age: values.age || null,
        currenteducationlevel: values.currenteducationlevel || null,
        schoolname: values.schoolname || null,
      };

      if (selectedStudent?.id) {
        // Update existing linked student
        await axios.put(`${API_URL}/linked-student/${selectedStudent.id}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        message.success('Linked student updated successfully');
      } else {
        // Create new linked student
        const createPayload = { ...payload, main_user_id: mainUserId };
        await axios.post(`${API_URL}/linked-student`, createPayload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        message.success('Linked student created successfully');
      }

      setFormModalVisible(false);
      setSelectedStudent(null);
      fetchLinkedStudents(); // Refresh the list
      if (onUpdated) onUpdated(); // Notify parent component
    } catch (err) {
      console.error('Error saving linked student:', err);
      
      // Handle specific error messages
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      } else {
        message.error(`Failed to ${selectedStudent?.id ? 'update' : 'create'} linked student`);
      }
    }
  };

  const handleFormCancel = () => {
    form.resetFields();
    setFormModalVisible(false);
    setSelectedStudent(null);
  };

  // Render student cards
  const renderStudentCards = () => {
    if (linkedStudents.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No linked students found"
        >
          <Button type="primary" onClick={handleAddStudent}>
            Add First Student
          </Button>
        </Empty>
      );
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
        {linkedStudents.map((student) => (
          <Card
            key={student.id}
            size="small"
            hoverable
            actions={[
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEditStudent(student)}
                style={{ border: 'none' }}
              >
                Edit
              </Button>,
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteStudent(student)}
                style={{ border: 'none' }}
              >
                Delete
              </Button>,
            ]}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <Avatar 
                size={48} 
                icon={<UserOutlined />} 
                style={{ 
                  backgroundColor: student.gender === 'male' ? '#1890ff' : student.gender === 'female' ? '#f759ab' : '#52c41a',
                  marginRight: '12px'
                }}
              />
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                  {student.firstname} {student.lastname}
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  {student.nickname}
                </div>
              </div>
            </div>
            
            <div style={{ marginBottom: '8px' }}>
              <Space wrap>
                {student.gender && (
                  <Tag color={student.gender === 'male' ? 'blue' : 'pink'}>
                    {student.gender.charAt(0).toUpperCase() + student.gender.slice(1)}
                  </Tag>
                )}
                {student.age && (
                  <Tag color="orange">Age: {student.age}</Tag>
                )}
              </Space>
            </div>

            {student.currenteducationlevel && (
              <div style={{ marginBottom: '4px', fontSize: '13px' }}>
                <strong>Education:</strong> {student.currenteducationlevel}
              </div>
            )}
            
            {student.schoolname && (
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>School:</strong> {student.schoolname}
              </div>
            )}
          </Card>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Main Modal - List View */}
      <Modal
        title="Manage Linked Students"
        open={visible}
        onCancel={onClose}
        width={1000}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddStudent}
            size="large"
          >
            Add New Linked Student
          </Button>
        </div>

        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '4px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>
          ) : (
            renderStudentCards()
          )}
        </div>
      </Modal>

      {/* Form Modal - Add/Edit */}
      <Modal
        open={formModalVisible}
        onCancel={handleFormCancel}
        title={selectedStudent?.id ? 'Edit Linked Student' : 'Add New Linked Student'}
        footer={[
          <Button key="cancel" onClick={handleFormCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            {selectedStudent?.id ? 'Update' : 'Create'}
          </Button>,
        ]}
        destroyOnClose={true}
        forceRender={true}
      >
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSubmit}
          preserve={false}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item 
                name="firstname" 
                label="First Name" 
                rules={[
                  { required: true, message: 'Please enter first name' },
                  { max: 50, message: 'First name must be less than 50 characters' }
                ]}
              >
                <Input placeholder="Enter first name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="lastname" 
                label="Last Name" 
                rules={[
                  { required: true, message: 'Please enter last name' },
                  { max: 50, message: 'Last name must be less than 50 characters' }
                ]}
              >
                <Input placeholder="Enter last name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item 
                name="nickname" 
                label="Nickname" 
                rules={[
                  { required: true, message: 'Please enter nickname' },
                  { max: 30, message: 'Nickname must be less than 30 characters' }
                ]}
              >
                <Input placeholder="Enter nickname" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="gender" label="Gender">
                <Select allowClear placeholder="Select gender">
                  <Option value="male">Male</Option>
                  <Option value="female">Female</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item 
                name="age" 
                label="Age"
                rules={[
                  { type: 'number', min: 1, max: 100, message: 'Age must be between 1 and 100' }
                ]}
              >
                <InputNumber 
                  min={1} 
                  max={100} 
                  style={{ width: '100%' }} 
                  placeholder="Enter age"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="currenteducationlevel" 
                label="Education Level"
                rules={[
                  { max: 100, message: 'Education level must be less than 100 characters' }
                ]}
              >
                <EducationLevelInput placeholder="e.g., Y6, G5, M1, etc." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            name="schoolname" 
            label="School Name"
            rules={[
              { max: 200, message: 'School name must be less than 200 characters' }
            ]}
          >
            <SchoolNameInput placeholder="Enter school name" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EditLinkedStudentForm;